import { defaultDiscoveredInvoiceDemoData } from 'pages/Settings/pages/DevToolsSettings';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GraphQLErrorPolicies } from 'shared/enums/GraphQLErrorPolicies';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import usePersistedState from 'shared/hooks/usePersistedState';
import {
  InvoiceStatus as StripeInvoiceStatus,
  useStripeTerminalProvider,
} from 'shared/providers/StripeTerminalProvider';
import { StripeTerminalClinicPetParent } from 'shared/providers/StripeTerminalProvider/types/StripeTerminalClinicPetParent';
import {
  BitwerxClientBalance,
  Invoice,
  InvoiceForClientFragment,
  InvoiceStatus,
  PaymentRelationship,
  StripePaymentIntentStatus,
  StripePaymentIntentStatusFragment,
  useGetBalanceForClientLazyQuery,
  useGetInvoicesForClientLazyQuery,
} from 'shared/types/graphql';
import { getBalanceTerminology } from 'shared/utils';
import styled from 'styled-components/macro';
import { integrationSystemDisplayName, useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { PersistedStateKeys } from 'shared/enums';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Link } from '@televet/kibble-ui/build/components/Link';
import usePaymentWritebackConfig from 'shared/hooks/usePaymentWritebackConfig';
import AccountBalanceSelector from 'shared/components/Invoicing/AccountBalanceSelector';
import InvoiceSelector from 'shared/components/Invoicing/InvoiceSelector';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SidePanelInvoicingEvents } from 'shared/providers/TrackingProvider/EventNames';

interface IDiscoveredInvoicesProps {
  clinicPetParent?: StripeTerminalClinicPetParent;
  preSelectedInvoiceIds: string[];
  manualAmount?: number;
  setSelectedInvoices: React.Dispatch<React.SetStateAction<InvoiceForClientFragment[]>>;
  setSelectedBalanceAmount: React.Dispatch<React.SetStateAction<number>>;
}
const DiscoveredInvoices = ({
  clinicPetParent,
  preSelectedInvoiceIds,
  manualAmount,
  setSelectedInvoices,
  setSelectedBalanceAmount,
}: IDiscoveredInvoicesProps): JSX.Element => {
  const [isDiscoveredInvoiceDemoModeEnabled] = usePersistedState(
    PersistedStateKeys.IsDiscoveredInvoiceDemoModeEnabled,
    false,
  );
  const [discoveredInvoiceDemoData] = usePersistedState(
    PersistedStateKeys.DiscoveredInvoiceDemoData,
    defaultDiscoveredInvoiceDemoData,
  );
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>(preSelectedInvoiceIds);
  const [emptyInvoiceStateMessage, setEmptyInvoiceStateMessage] = useState('There are no pending invoices');
  const [isSimulatedBalanceQueryLoading, setIsSimulatedBalanceQueryLoading] = useState(false);
  const [isSimulatedInvoiceQueryLoading, setIsSimulatedInvoiceQueryLoading] = useState(false);

  const [selectedBalances, setSelectedBalances] = useState<{ [key: string]: number }>({});

  const { primaryIntegrationName, isInvoicePreviewSupported } = useIntegrationsProvider();
  const { isWritebackManualPaymentEnabled } = usePaymentWritebackConfig();

  const [validationMessage, setValidationMessage] = useState('');

  const isPimsClinicPetParent = useMemo(() => {
    return clinicPetParent?.pimsId !== null;
  }, [clinicPetParent?.pimsId]);

  const [getInvoices, { data, loading: isInvoiceQueryLoading }] = useGetInvoicesForClientLazyQuery({
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    errorPolicy: GraphQLErrorPolicies.All,
    onError: () => {
      setEmptyInvoiceStateMessage('Error connecting to invoice data, please wait a moment and try again');
    },
    onCompleted: () => {
      setEmptyInvoiceStateMessage('There are no pending invoices');
    },
  });

  const [getBalance, { data: balanceData, loading: isBalanceQueryLoading }] = useGetBalanceForClientLazyQuery({
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    errorPolicy: GraphQLErrorPolicies.All,
  });

  const balanceTerminology = useMemo(() => {
    return getBalanceTerminology(primaryIntegrationName);
  }, [primaryIntegrationName]);

  const {
    setSelectedPetParent: setSelectedInvoicingPetParent,
    currentInvoiceStatus,
    setInvoiceIds,
    setPaymentRelationship,
    isBalancePreSelected,
    setIsBalancePreSelected,
  } = useStripeTerminalProvider();

  const { trackEvent } = useTracking();

  const petParentId = clinicPetParent?.id;

  const isPendingPaymentIntents = (intents: StripePaymentIntentStatusFragment[]): boolean => {
    for (const intent of intents) {
      if (intent?.status === StripePaymentIntentStatus.RequiresPaymentMethod) {
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    setSelectedInvoiceIds(preSelectedInvoiceIds);
  }, [preSelectedInvoiceIds]);

  const fetchInvoices = useCallback(() => {
    // If we can't do anything with invoices, don't fetch them (wasteful query to Bitwerx)
    if (!isInvoicePreviewSupported) {
      return;
    }

    if (isDiscoveredInvoiceDemoModeEnabled) {
      setIsSimulatedInvoiceQueryLoading(true);
      setTimeout(() => {
        setIsSimulatedInvoiceQueryLoading(false);
      }, 2500);
    }
    if (petParentId && isPimsClinicPetParent) {
      getInvoices({
        variables: {
          where: {
            clinicPetParentId: { equals: petParentId },
            status: { equals: InvoiceStatus.Open },
            total: { gt: 0 },
            stripePaymentIntents: { none: { status: { equals: StripePaymentIntentStatus.Succeeded } } },
          },
        },
      });
    }
  }, [getInvoices, isInvoicePreviewSupported, isDiscoveredInvoiceDemoModeEnabled, isPimsClinicPetParent, petParentId]);

  const fetchBalance = useCallback(() => {
    if (isDiscoveredInvoiceDemoModeEnabled) {
      setIsSimulatedBalanceQueryLoading(true);
      setTimeout(() => {
        setIsSimulatedBalanceQueryLoading(false);
      }, 1500);
    }
    if (petParentId && isPimsClinicPetParent) {
      getBalance({
        variables: {
          where: {
            clinicPetParentId: petParentId,
          },
        },
      });
    }
  }, [getBalance, isDiscoveredInvoiceDemoModeEnabled, isPimsClinicPetParent, petParentId]);

  useEffect(() => {
    setSelectedBalances({});
    if (petParentId) {
      fetchInvoices();
      fetchBalance();
    }
  }, [fetchBalance, fetchInvoices, petParentId]);

  const invoices: InvoiceForClientFragment[] = useMemo(() => {
    if (isDiscoveredInvoiceDemoModeEnabled) {
      return discoveredInvoiceDemoData.invoices as unknown as Invoice[];
    }
    return data?.getInvoicesForClient || [];
  }, [data?.getInvoicesForClient, discoveredInvoiceDemoData.invoices, isDiscoveredInvoiceDemoModeEnabled]);

  const balance: BitwerxClientBalance = useMemo(() => {
    if (isDiscoveredInvoiceDemoModeEnabled) {
      return discoveredInvoiceDemoData.balance;
    }
    return balanceData?.getBalanceForClient || {};
  }, [balanceData?.getBalanceForClient, discoveredInvoiceDemoData.balance, isDiscoveredInvoiceDemoModeEnabled]);

  useEffect(() => {
    if (!invoices) return;

    const selectedInvoices = invoices.filter(({ id }) => selectedInvoiceIds.includes(id));
    setSelectedInvoices(selectedInvoices);
  }, [invoices, selectedInvoiceIds, setSelectedInvoices]);

  useEffect(() => {
    if (balance === undefined) return;

    let amount = 0;

    for (const balance in selectedBalances) {
      amount += selectedBalances[balance];
    }

    setSelectedBalanceAmount(amount);
  }, [balance, selectedBalances, setSelectedBalanceAmount]);

  const invoicesTotalAmount = useMemo(() => {
    let total = (manualAmount || 0) * 100;

    if (invoices) {
      for (const invoiceId of selectedInvoiceIds) {
        const invoice = invoices.find((invoice) => invoice.id === invoiceId);
        total += invoice?.total ?? 0;
      }
    }

    if (balance) {
      for (const balanceId in selectedBalances) {
        total += selectedBalances[balanceId] ?? 0;
      }
    }

    if (!!Object.keys(selectedBalances).length || !!selectedInvoiceIds.length) {
      if (total < 0) {
        setValidationMessage('Cannot collect negative payment amount');
      }

      if (total < 50 && total >= 0) {
        setValidationMessage('Cannot collect amount less than $0.50');
      }
    } else {
      setValidationMessage('');
    }

    if (total > 0) {
      setValidationMessage('');
      return total;
    } else return 0;
  }, [balance, invoices, manualAmount, selectedBalances, selectedInvoiceIds]);

  const isBalanceLoading = useMemo(() => {
    if (isDiscoveredInvoiceDemoModeEnabled) {
      return isSimulatedBalanceQueryLoading;
    }
    return isBalanceQueryLoading;
  }, [isBalanceQueryLoading, isDiscoveredInvoiceDemoModeEnabled, isSimulatedBalanceQueryLoading]);

  const isInvoiceLoading = useMemo(() => {
    if (isDiscoveredInvoiceDemoModeEnabled) {
      return isSimulatedInvoiceQueryLoading;
    }
    return isInvoiceQueryLoading;
  }, [isDiscoveredInvoiceDemoModeEnabled, isInvoiceQueryLoading, isSimulatedInvoiceQueryLoading]);

  const onBalanceSelected = useCallback(
    (clinicPetParentId: string): void => {
      if (isBalanceLoading) return;
      if (!selectedBalances[clinicPetParentId]) {
        setSelectedBalances((prevState): { [key: string]: number } => {
          const newState = { ...prevState };
          const clientBalance = balance.currentBalance || 0;
          if (clientBalance !== 0) {
            setPaymentRelationship(PaymentRelationship.AccountBalance);
            newState[clinicPetParentId] = clientBalance;
          }
          return newState;
        });
      } else {
        setIsBalancePreSelected(false);
        setSelectedBalances((prevState): { [key: string]: number } => {
          if (!isWritebackManualPaymentEnabled) {
            setPaymentRelationship(null);
          }
          const newState = { ...prevState };
          delete newState[clinicPetParentId];
          return newState;
        });
      }
    },
    [
      isBalanceLoading,
      selectedBalances,
      balance.currentBalance,
      setPaymentRelationship,
      setIsBalancePreSelected,
      isWritebackManualPaymentEnabled,
    ],
  );

  const sendToSidePanel = useCallback((): void => {
    if (currentInvoiceStatus !== StripeInvoiceStatus.Pending) {
      setSelectedInvoicingPetParent(clinicPetParent);
      setInvoiceIds(selectedInvoiceIds);
    }
  }, [clinicPetParent, currentInvoiceStatus, selectedInvoiceIds, setInvoiceIds, setSelectedInvoicingPetParent]);

  useEffect(() => {
    if (isBalancePreSelected && clinicPetParent)
      setSelectedBalances((prevState): { [key: string]: number } => {
        setPaymentRelationship(PaymentRelationship.AccountBalance);
        const newState = { ...prevState };
        newState[clinicPetParent.id] = balance.currentBalance || 0;
        return newState;
      });
  }, [balance.currentBalance, clinicPetParent, isBalancePreSelected, setPaymentRelationship]);

  useEffect(() => {
    sendToSidePanel();
  }, [invoicesTotalAmount]); // eslint-disable-line react-hooks/exhaustive-deps

  if (clinicPetParent?.id) {
    return isPimsClinicPetParent ? (
      <Wrapper>
        <HR />
        <DrawerHeader>
          <Text data-testid="client-account-drawer" fontWeight="bold">
            Client Account
          </Text>
          <Link
            data-testid="ar-balance-refresh-button"
            size="sm"
            mr={3}
            variant={isBalanceLoading ? 'subtle' : 'interactive'}
            iconProps={{
              name: 'doubleArrowCircle',
            }}
            onClick={(): void => {
              if (isBalanceLoading) return;

              trackEvent(SidePanelInvoicingEvents.InvoiceRefreshPimsBalance);
              setSelectedBalances({});
              fetchBalance();
            }}
          >
            Refresh
          </Link>
        </DrawerHeader>
        <AccountBalanceSelector
          balanceTerminology={balanceTerminology}
          currentBalance={balance.currentBalance || 0}
          isChecked={!!selectedBalances[clinicPetParent.id]}
          onClick={(): void => onBalanceSelected(clinicPetParent.id)}
          showSkeleton={isBalanceLoading}
        />
        {isInvoicePreviewSupported && (
          <>
            <DrawerHeader>
              <Text data-testid="client-invoices-drawer" fontWeight="bold">
                Client Invoices ({invoices.length})
              </Text>
              <Link
                data-testid="invoice-refresh-button"
                size="sm"
                mr={3}
                variant={isInvoiceLoading ? 'subtle' : 'interactive'}
                iconProps={{
                  name: 'doubleArrowCircle',
                }}
                onClick={(): void => {
                  if (isInvoiceLoading) return;

                  trackEvent(SidePanelInvoicingEvents.InvoiceRefreshInvoices);
                  setSelectedInvoiceIds([]);
                  fetchInvoices();
                }}
              >
                Refresh
              </Link>
            </DrawerHeader>
            <>
              {!invoices.length && !isInvoiceLoading && <DrawerSubTitle>{emptyInvoiceStateMessage}</DrawerSubTitle>}
              <Invoices>
                {isInvoiceLoading ? (
                  <InvoiceSelector
                    selectedInvoiceIds={selectedInvoiceIds}
                    isPendingPaymentIntents={isPendingPaymentIntents}
                    setSelectedInvoiceIds={setSelectedInvoiceIds}
                    showSkeleton={true}
                  />
                ) : (
                  invoices.map((invoice) => {
                    return (
                      <InvoiceSelector
                        invoice={invoice}
                        selectedInvoiceIds={selectedInvoiceIds}
                        setSelectedInvoiceIds={setSelectedInvoiceIds}
                        isPendingPaymentIntents={isPendingPaymentIntents}
                        key={invoice.id}
                        showSkeleton={isInvoiceLoading}
                      />
                    );
                  })
                )}
              </Invoices>
              {!!validationMessage && (
                <Alert
                  title={validationMessage}
                  status="error"
                  mx={3}
                  hideCloseButton
                  titleProps={{ fontSize: 'sm' }}
                />
              )}
            </>
          </>
        )}
      </Wrapper>
    ) : (
      <Wrapper>
        <HR />
        <DrawerSubTitle>{`Unable to pull ${
          primaryIntegrationName === integrationSystemDisplayName.AVIMARK ? '' : 'invoices or'
        } ${balanceTerminology.toLocaleLowerCase()} for clients not in ${
          primaryIntegrationName ?? 'your PIMS'
        }`}</DrawerSubTitle>
      </Wrapper>
    );
  }
  return <></>;
};

export default DiscoveredInvoices;

const Wrapper = styled.div``;

const DrawerHeader = styled.div`
  padding: 10px 5px 0 10px;
  display: flex;
  justify-content: space-between;
`;

const DrawerSubTitle = styled.div`
  margin: 10px 0;
  padding: 0 10px;
`;

const Invoices = styled.div`
  max-height: 140px;
  overflow-y: auto;
  padding: 0 10px 10px 10px;
`;

const HR = styled.div`
  height: 1px;
  border-bottom: solid 1px #d2d2d2;
`;
